import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import apiTypes from 'src/api/optimalprint-sdk.d';
import sendPostMessage from 'src/store/app/operation/sendPostMessage';
import { MESSAGE_TYPE } from 'src/store/app/types';
import { Store } from 'src/store/index';
import getDesignId from 'src/store/design/selector/getDesignId';
import { addGalleryImagesAction } from 'src/store/gallery/slice';
import { IClipArt, IClipArtResultCategory } from 'src/store/addon/types/clipArtTypes';

function inferMimeType(url: string): string {
  const lower = url.toLowerCase().split('?')[0];
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.pdf')) return 'application/pdf';
  return 'image/png';
}

function findClipArtById(
  categories: { [id: string]: IClipArtResultCategory },
  addonId: string,
): IClipArt | undefined {
  let result: IClipArt | undefined;
  Object.values(categories).some((category) => {
    const found = category.clipArts?.find((c) => String(c.clipArtId) === addonId);
    if (found) {
      result = found;
      return true;
    }
    if (category.subCategories) {
      const subMap: { [id: string]: IClipArtResultCategory } = {};
      category.subCategories.forEach((sub) => { subMap[sub.clipArtCategoryId] = sub; });
      const subResult = findClipArtById(subMap, addonId);
      if (subResult) {
        result = subResult;
        return true;
      }
    }
    return false;
  });
  return result;
}

const handleAddonUploadOperation = (data: { addonId: string }) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  const { addonId } = data;
  const store = getState();
  const clipArt = findClipArtById(store.addon.clipArtCategories, addonId);

  if (!clipArt) {
    // eslint-disable-next-line no-console
    console.error('[OP-39631] Cannot find clipart for addon upload:', addonId);
    sendPostMessage({
      type: MESSAGE_TYPE['addons.addonUploadFailed'],
      data: { addonId },
    });
    return;
  }

  try {
    const customerDesignId = getDesignId(store);
    const request: any = {
      filename: clipArt.title || `addon-${addonId}`,
      sourcePictureUrl: clipArt.imageUrl,
      url: clipArt.imageUrl,
    };
    if (customerDesignId) {
      request.customerDesignId = customerDesignId;
    }

    const response = (await opApiRequest(
      endpoints.GALLERY_V1_IMPORT,
      request,
      'data',
      'POST',
    )) as apiTypes.AppBundle.Api.Entity.Photo.V1.IPhoto;

    if (response && response.url) {
      dispatch(addGalleryImagesAction([response]));

      sendPostMessage({
        type: MESSAGE_TYPE['addons.addonUploaded'],
        data: {
          addonId,
          assetId: String(response.id),
          url: response.url,
          thumbnailUrl: response.url,
          mimeType: inferMimeType(clipArt.imageUrl),
        },
      });
    } else {
      // eslint-disable-next-line no-console
      console.error('[OP-39631] Addon import returned no URL:', response);
      sendPostMessage({
        type: MESSAGE_TYPE['addons.addonUploadFailed'],
        data: { addonId },
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[OP-39631] Addon upload failed:', error);
    sendPostMessage({
      type: MESSAGE_TYPE['addons.addonUploadFailed'],
      data: { addonId },
    });
  }
};

export default handleAddonUploadOperation;
